import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {boolean} */
  #hasCheckedInitialState = false;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#ensureClosedAfterRestore();
    this.#groupLookLines();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#groupLookLines();
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const isNormalProductAdd = event.context === 'product';
    const shouldAutoOpen =
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen &&
      (this.hasAttribute('auto-open') || isNormalProductAdd);

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    event.promise
      ?.then(async ({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = async () => {
          await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
          this.#groupLookLines();
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #ensureClosedAfterRestore() {
    if (this.#hasCheckedInitialState) return;
    this.#hasCheckedInitialState = true;

    this.#clearPersistedCartDrawerState();

    const drawer = this.#themeDrawer;
    if (!drawer?.hasAttribute('open')) return;

    const isPageHydrating = document.readyState === 'loading' || document.readyState === 'interactive';
    if (!isPageHydrating) return;

    const dialog = this.#dialog;
    drawer.removeAttribute('open');
    drawer.style.removeProperty('--drawer-stack-order');
    dialog?.classList.remove(
      'theme-drawer__dialog-instant',
      'theme-drawer__dialog--opening',
      'theme-drawer__dialog--opening-inline-start',
      'theme-drawer__dialog--closing'
    );
    if (dialog?.open) dialog.close();
    dialog?.removeAttribute('open');

    if (!document.querySelector('theme-drawer[open]')) {
      document.querySelector('.page-wrapper')?.classList.remove('page-wrapper--drawer-open');
    }
  }

  #clearPersistedCartDrawerState() {
    try {
      const savedDrawers = sessionStorage.getItem('theme-drawer-open');
      if (!savedDrawers) return;

      const nextDrawers = savedDrawers
        .split(',')
        .filter(Boolean)
        .filter((drawerId) => drawerId !== 'cart-drawer');

      if (nextDrawers.length) {
        sessionStorage.setItem('theme-drawer-open', nextDrawers.join(','));
      } else {
        sessionStorage.removeItem('theme-drawer-open');
      }
    } catch (error) {
      console.warn('[cart-drawer] Unable to clear persisted drawer state:', error);
    }
  }

  #groupLookLines() {
    const lines = /** @type {HTMLUListElement | null} */ (this.querySelector('[data-cart-drawer-lines]'));
    if (!lines) return;

    const lookLines = Array.from(lines.querySelectorAll('[data-fill-cart-drawer-look-id]')).filter(
      (line) => line instanceof HTMLLIElement && line.parentElement === lines
    );
    if (!lookLines.length) return;

    /** @type {Map<string, HTMLLIElement[]>} */
    const groupedLines = new Map();
    for (const line of lookLines) {
      const lookId = line.dataset.fillCartDrawerLookId;
      if (!lookId) continue;
      const group = groupedLines.get(lookId) ?? [];
      group.push(line);
      groupedLines.set(lookId, group);
    }

    for (const [lookId, groupLines] of groupedLines) {
      const firstLine = groupLines[0];
      if (!firstLine || groupLines.some((line) => line.closest('[data-fill-cart-drawer-look-group]'))) continue;

      const lookName = firstLine.dataset.fillCartDrawerLookName || 'Shop the Look';
      const lookParent = firstLine.dataset.fillCartDrawerLookParent || '';
      const headingId = `CartDrawerLook-${lookId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
      const groupItem = document.createElement('li');
      groupItem.className = 'fill-cart-drawer__look-group';
      groupItem.dataset.fillCartDrawerLookGroup = lookId;

      const groupCard = document.createElement('section');
      groupCard.className = 'fill-cart-drawer__look-card';
      groupCard.setAttribute('aria-labelledby', headingId);

      const header = document.createElement('header');
      header.className = 'fill-cart-drawer__look-header';

      const copy = document.createElement('div');
      const label = document.createElement('p');
      label.className = 'fill-cart-drawer__look-label';
      label.textContent = 'Shop the Look';

      const title = document.createElement('h3');
      title.className = 'fill-cart-drawer__look-title';
      title.id = headingId;
      title.textContent = lookName;

      const count = document.createElement('p');
      count.className = 'fill-cart-drawer__look-count';
      count.textContent = `${groupLines.length} ${groupLines.length === 1 ? 'piece' : 'pieces'}`;

      const list = document.createElement('ul');
      list.className = 'fill-cart-drawer__look-items';
      list.setAttribute('role', 'list');

      copy.append(label, title);
      header.append(copy, count);
      groupCard.append(header, list);

      if (lookParent) groupItem.dataset.fillCartDrawerLookParent = lookParent;
      groupItem.append(groupCard);
      lines.insertBefore(groupItem, firstLine);

      for (const line of groupLines) {
        line.classList.add('fill-cart-drawer__line--look-child');
        list.append(line);
      }
    }
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
